/**
 * Line manipulation utilities
 * Extracted from MyCanvas.js to reduce file size
 */

import { v4 as uuidv4 } from "uuid";

// Counter to ensure uniqueness even within the same millisecond
let idCounter = 0;

/**
 * Generate a marker ID for new elements
 * Uses timestamp, counter, and UUID to ensure absolute uniqueness
 * @returns {string} Generated marker ID
 */
export const generateMarkerId = () => {
  idCounter = (idCounter + 1) % 1000000; // Reset counter if it gets too large
  return `marker-${Date.now()}-${idCounter}-${uuidv4()}`;
};

/**
 * Create a function to handle dot drag move for line endpoints
 * @param {Array} circles - Current circles array (line dots)
 * @param {Function} setLines - Setter for lines array
 * @param {Function} setCircles - Setter for circles array
 * @returns {Function} Function to handle dot drag move
 */
export const createHandleDotDragMove =
  (circles, setLines, setCircles) => (updatedLine) => {
    // Update the line
    setLines((prevLines) =>
      prevLines.map((line) => (line.id === updatedLine.id ? updatedLine : line))
    );

    // Update the corresponding dots
    setCircles((prevCircles) =>
      prevCircles.map((dot) => {
        if (dot.lineId !== updatedLine.id) return dot;
        const pointIndex = dot.pointIndex;
        // Calculate dot position relative to line position
        const newX = updatedLine.points[pointIndex * 2] - (updatedLine.x || 0);
        const newY =
          updatedLine.points[pointIndex * 2 + 1] - (updatedLine.y || 0);
        return { ...dot, x: newX, y: newY };
      })
    );
  };

/**
 * Create dots for a line element
 * @param {Object} line - Line object to create dots for
 * @returns {Array} Array of two dot objects (start and end)
 */
export const createLineDots = (line) => {
  // Use the line's stroke color for the dots, fallback to black if not available
  const dotColor = line.strokeColor || line.stroke || line.fillColor || "black";

  // Use the line's points directly - they are already in the correct coordinate system
  // The LineElement component handles the positioning relative to the group
  // Calculate dot positions relative to the line's position
  const startDot = {
    id: `dot-${line.id}-start`,
    x: line.points[0] - (line.x || 0),
    y: line.points[1] - (line.y || 0),
    radius: 4, // Smaller radius for better appearance
    fill: dotColor,
    stroke: dotColor,
    strokeWidth: 1,
    lineId: line.id,
    pointIndex: 0,
  };
  const endDot = {
    id: `dot-${line.id}-end`,
    x: line.points[2] - (line.x || 0),
    y: line.points[3] - (line.y || 0),
    radius: 4, // Smaller radius for better appearance
    fill: dotColor,
    stroke: dotColor,
    strokeWidth: 1,
    lineId: line.id,
    pointIndex: 1,
  };
  return [startDot, endDot];
};

/**
 * Create a function to add a new line to the canvas
 * @param {Function} setLines - Setter for lines array
 * @param {Function} setCircles - Setter for circles array
 * @param {Function} saveToHistory - Function to save to history
 * @param {string} fillColor - Current fill color
 * @param {Function} generateMarkerId - Function to generate marker IDs
 * @returns {Function} Function to add a new line
 */
export const createAddLine =
  (setLines, setCircles, saveToHistory, fillColor, generateMarkerId) =>
    (points, x = 0, y = 0) => {
      const newLineId = generateMarkerId();
      let newLine = null;

      setLines((prev) => {
        const lineNumber = prev.length + 1;
        newLine = {
          id: newLineId,
          name: `Stroke ${lineNumber}`,
          x: x,
          y: y,
          points,
          strokeColor: fillColor || "black",
          strokeWidth: 2,
          stroke: fillColor || "black",
          fillColor: fillColor || "black",
          pointerAtBeginning: false,
          pointerAtEnd: false,
        };
        return [...prev, newLine];
      });

      if (newLine) {
        setCircles((prev) => [...prev, ...createLineDots(newLine)]);
      }
      saveToHistory();
    };

/**
 * Calculate line points from position, length, and angle
 * @param {number} x - Starting X coordinate
 * @param {number} y - Starting Y coordinate
 * @param {number} length - Length of the line
 * @param {number} angleInDegrees - Angle in degrees
 * @returns {Array<number>} Array of points [x1, y1, x2, y2]
 */
export const calculateLinePoints = (x, y, length, angleInDegrees) => {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  const endX = x + Math.cos(angleInRadians) * length;
  const endY = y + Math.sin(angleInRadians) * length;
  return [x, y, endX, endY];
};

/**
 * Handles line drag end operation
 * @param {string} id - Line ID
 * @param {number} x - New x position
 * @param {number} y - New y position
 * @param {Array} lines - Current lines array
 * @param {Function} setLines - Function to set lines
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @param {Object} currentLine - Optional: pass current line object to preserve all properties (rotation, scaleX, scaleY, etc.)
 */
export const handleLineDragEnd = (
  id,
  x,
  y,
  lines,
  setLines,
  saveToHistory,
  isUndoingRedoing,
  currentLine = null, // Optional: pass current line object to preserve all properties (rotation, scaleX, scaleY, etc.)
  setSelectedObject = null, // Optional: function to update selectedObject
  setSelectedObjects = null, // Optional: function to update selectedObjects
  offCanvasLines = null, // Optional: off-canvas lines array
  setOffCanvasLines = null // Optional: setter for off-canvas lines array
) => {
  // Resolve target canvas from current snapshots first so we avoid setter ordering races.
  const isInMainCanvas = Array.isArray(lines)
    ? lines.some((line) => line.id === id)
    : false;
  const isInOffCanvas = Array.isArray(offCanvasLines)
    ? offCanvasLines.some((line) => line.id === id)
    : false;

  let lineFound = isInMainCanvas || isInOffCanvas;
  let updatedLineForSelection = null;

  // Update main canvas line
  if (lineFound && isInMainCanvas && setLines) {
    setLines((prevLines) => {
      // Check if line is in this array
      const lineIndex = prevLines.findIndex((line) => line.id === id);
      if (lineIndex === -1) {
        // Line not in this array, return unchanged
        return prevLines;
      }

      // Line found in main canvas
      lineFound = true;

      // CRITICAL: Find the current line to preserve all properties
      const existingLine = currentLine || prevLines[lineIndex];

      // CRITICAL: Check if points are absolute coordinates and need to be updated
      let updatedPoints = existingLine.points;
      if (existingLine.points && existingLine.points.length >= 4 &&
        existingLine.x !== undefined && existingLine.y !== undefined) {
        const firstPointX = existingLine.points[0];
        const firstPointY = existingLine.points[1];
        const tolerance = 1.0;
        const pointsAreAbsolute =
          Math.abs(firstPointX - existingLine.x) < tolerance &&
          Math.abs(firstPointY - existingLine.y) < tolerance;

        if (pointsAreAbsolute) {
          const deltaX = x - existingLine.x;
          const deltaY = y - existingLine.y;
          updatedPoints = existingLine.points.map((coord, i) => {
            return i % 2 === 0 ? coord + deltaX : coord + deltaY;
          });
        }
      }

      // Update the line with new position
      const updatedLines = prevLines.map((line) =>
        line.id === id
          ? {
            ...existingLine,
            x,
            y,
            points: updatedPoints,
          }
          : line
      );

      // Store updated line for selection update
      updatedLineForSelection = updatedLines.find((line) => line.id === id);

      // Update selectedObject and selectedObjects
      if ((setSelectedObject || setSelectedObjects) && updatedLineForSelection) {
        if (setSelectedObject) {
          setSelectedObject((prev) => {
            if (prev?.id === id) {
              return { ...prev, ...updatedLineForSelection, x, y, points: updatedPoints };
            }
            return prev;
          });
        }
        if (setSelectedObjects) {
          setSelectedObjects((prev) => {
            if (Array.isArray(prev)) {
              return prev.map((obj) =>
                obj.id === id
                  ? { ...obj, ...updatedLineForSelection, x, y, points: updatedPoints }
                  : obj
              );
            }
            return prev;
          });
        }
      }

      // Save to history
      if (saveToHistory && (!isUndoingRedoing || !isUndoingRedoing.current)) {
        setTimeout(() => {
          saveToHistory("line_drag_end", null, null, null, updatedLines);
        }, 0);
      }

      return updatedLines;
    });
  }

  // Update off-canvas line
  if (lineFound && isInOffCanvas && setOffCanvasLines) {
    setOffCanvasLines((prevLines) => {
      // Check if line is in this array
      const lineIndex = prevLines.findIndex((line) => line.id === id);
      if (lineIndex === -1) {
        // Line not in this array either
        return prevLines;
      }

      // Line found in off-canvas
      lineFound = true;

      // CRITICAL: Find the current line to preserve all properties
      const existingLine = currentLine || prevLines[lineIndex];

      // CRITICAL: Check if points are absolute coordinates and need to be updated
      let updatedPoints = existingLine.points;
      if (existingLine.points && existingLine.points.length >= 4 &&
        existingLine.x !== undefined && existingLine.y !== undefined) {
        const firstPointX = existingLine.points[0];
        const firstPointY = existingLine.points[1];
        const tolerance = 1.0;
        const pointsAreAbsolute =
          Math.abs(firstPointX - existingLine.x) < tolerance &&
          Math.abs(firstPointY - existingLine.y) < tolerance;

        if (pointsAreAbsolute) {
          const deltaX = x - existingLine.x;
          const deltaY = y - existingLine.y;
          updatedPoints = existingLine.points.map((coord, i) => {
            return i % 2 === 0 ? coord + deltaX : coord + deltaY;
          });
        }
      }

      // Update the line with new position
      const updatedLines = prevLines.map((line) =>
        line.id === id
          ? {
            ...existingLine,
            x,
            y,
            points: updatedPoints,
          }
          : line
      );

      // Store updated line for selection update
      updatedLineForSelection = updatedLines.find((line) => line.id === id);

      // Update selectedObject and selectedObjects
      if ((setSelectedObject || setSelectedObjects) && updatedLineForSelection) {
        if (setSelectedObject) {
          setSelectedObject((prev) => {
            if (prev?.id === id) {
              return { ...prev, ...updatedLineForSelection, x, y, points: updatedPoints };
            }
            return prev;
          });
        }
        if (setSelectedObjects) {
          setSelectedObjects((prev) => {
            if (Array.isArray(prev)) {
              return prev.map((obj) =>
                obj.id === id
                  ? { ...obj, ...updatedLineForSelection, x, y, points: updatedPoints }
                  : obj
              );
            }
            return prev;
          });
        }
      }

      // Save to history (use line_drag_end_off_canvas so historyUtils does not defer when editing;
      // this ensures the moved position is saved and one undo only removes main-canvas adds)
      if (saveToHistory && (!isUndoingRedoing || !isUndoingRedoing.current)) {
        setTimeout(() => {
          saveToHistory("line_drag_end_off_canvas", null, null, null, null, null, null, null, null, null, null, null, null, null, updatedLines);
        }, 0);
      }

      return updatedLines;
    });
  }

  if (!lineFound) {
    console.warn("handleLineDragEnd: line not found in either array", { id, linesCount: lines?.length, offCanvasLinesCount: offCanvasLines?.length });
  }
};

/**
 * Create a function to handle line drag end with bound dependencies
 * @param {Array} lines - Current lines array
 * @param {Function} setLines - Function to set lines
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Function} Function to handle line drag end
 */
// Track last drag end call to prevent duplicates
let lastLineDragEndCall = null;
const LINE_DRAG_END_DEBOUNCE_MS = 50; // 50ms debounce window

export const createHandleLineDragEnd = (
  lines,
  setLines,
  saveToHistory,
  isUndoingRedoing,
  setSelectedObject = null, // Optional: function to update selectedObject
  setSelectedObjects = null, // Optional: function to update selectedObjects
  offCanvasLines = null, // Optional: off-canvas lines array
  setOffCanvasLines = null // Optional: setter for off-canvas lines array
) => {
  return (id, x, y, currentLine = null) => {
    // CRITICAL: Debounce duplicate calls to prevent creating duplicate history entries
    // This handles cases where handleLineDragEnd is called twice (e.g., from React strict mode or multiple event handlers)
    const now = Date.now();
    const callKey = `${id}-${x}-${y}`;

    if (
      lastLineDragEndCall &&
      lastLineDragEndCall.key === callKey &&
      now - lastLineDragEndCall.timestamp < LINE_DRAG_END_DEBOUNCE_MS
    ) {
      return; // Skip duplicate call
    }

    // Update last call tracking
    lastLineDragEndCall = {
      key: callKey,
      timestamp: now,
    };

    return handleLineDragEnd(
      id,
      x,
      y,
      lines,
      setLines,
      saveToHistory,
      isUndoingRedoing,
      currentLine, // Pass the current line object to preserve all properties (rotation, scaleX, scaleY, etc.)
      setSelectedObject, // Pass setSelectedObject to update selection after drag
      setSelectedObjects, // Pass setSelectedObjects to update selection after drag
      offCanvasLines, // Pass off-canvas lines array
      setOffCanvasLines // Pass setter for off-canvas lines array
    );
  };
};
